import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container } from '@pixi/react';
import useEventListener from '@use-it/event-listener';
import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { createBetOutbound, createCallOutbound, createCheckOutbound, createFoldOutbound, createRaiseOutbound } from '../../../../api/outbound/pokerOutbounds';
import { isConfigFlagEnabled } from '../../../../config';
import { useInstanceId } from '../../../../context/instance/instanceContext';
import { useSocketSend } from '../../../../context/webSocket/webSocketContext';
import { thunkSetBetSliderAmount } from '../../../../redux/actions/pokerActions';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { isDialogOpen, isDrawerOpen } from '../../../../utils/uiUtils';
import { getTopWindow, isTestEnvironment } from '../../../../utils/utils';
import { getDecreasedBetAmount, getIncreasedBetAmount } from '../betUtils';
import { PixiAllInButton } from './pixiAllInButton';
import { PixiBetButton } from './pixiBetButton';
import { PixiCallButton } from './pixiCallButton';
import { PixiCheckButton } from './pixiCheckButton';
import { PixiFoldButton } from './pixiFoldButton';
import { PixiRaiseButton } from './pixiRaiseButton';
import { PixiSetBetButtonGroup } from './pixiSetBetButtonGroup';
const PIXI_DATA = isTestEnvironment() ? { type: 'bet-controls' } : null;
function shouldKeyDownBeIgnored(event, isKeyboardCapturingInputActive, instanceId) {
    if (isDialogOpen(instanceId) || isDrawerOpen(instanceId)) {
        return true;
    }
    if (isKeyboardCapturingInputActive) {
        if (!event.metaKey || !event.shiftKey) {
            // With this type of panel, keyboard input (without modifiers) is captured,
            // so metaKey and shiftKey are required to activate betting key combos.
            return true;
        }
    }
    else {
        if (event.metaKey && !event.shiftKey) {
            // chat panel is closed, ignore when metaKey is used without shift to preserve browser keys like meta+R to refresh
            return true;
        }
    }
    return false;
}
function onKeyDown(event, myTurnProperties, isKeyboardCapturingInputActive, instanceId, socketSend, dispatch) {
    if (shouldKeyDownBeIgnored(event, isKeyboardCapturingInputActive, instanceId)) {
        return;
    }
    const { betSequenceNumber, canBet, canCall, canCheck, canFold, canRaise, betSliderMinAmount, betSliderMaxAmount, betIncrement, betTotalAmountToCall, myTotalBetAmount, betSliderAmount } = myTurnProperties;
    function attemptBet() {
        if (!canBet) {
            return false;
        }
        socketSend(createBetOutbound(betSequenceNumber, betSliderAmount));
        event.preventDefault();
        return true;
    }
    function attemptRaise() {
        if (!canRaise) {
            return false;
        }
        const raiseAmount = betSliderAmount - (betTotalAmountToCall - myTotalBetAmount);
        socketSend(createRaiseOutbound(betSequenceNumber, raiseAmount));
        event.preventDefault();
        return true;
    }
    function attemptCall() {
        if (!canCall) {
            return false;
        }
        socketSend(createCallOutbound(betSequenceNumber));
        event.preventDefault();
        return true;
    }
    function attemptCheck() {
        if (!canCheck) {
            return false;
        }
        socketSend(createCheckOutbound(betSequenceNumber));
        event.preventDefault();
        return true;
    }
    function attemptFold() {
        if (!canFold) {
            return false;
        }
        socketSend(createFoldOutbound(betSequenceNumber));
        event.preventDefault();
        return true;
    }
    function increaseBet() {
        dispatch(thunkSetBetSliderAmount(getIncreasedBetAmount(betSliderAmount, betSliderMaxAmount, betIncrement)));
        event.preventDefault();
    }
    function decreaseBet() {
        dispatch(thunkSetBetSliderAmount(getDecreasedBetAmount(betSliderAmount, betSliderMinAmount, betIncrement)));
        event.preventDefault();
    }
    switch (event.key) {
        case 'b': {
            if (attemptBet()) {
                break;
            }
            attemptRaise();
            break;
        }
        case 'r': {
            if (attemptRaise()) {
                break;
            }
            attemptBet();
            break;
        }
        case 'Enter': {
            if (betSliderAmount > betSliderMinAmount) {
                if (attemptBet()) {
                    break;
                }
                attemptRaise();
            }
            else {
                if (attemptCall()) {
                    break;
                }
                attemptCheck();
            }
            break;
        }
        case 'c': {
            if (attemptCall()) {
                break;
            }
            attemptCheck();
            break;
        }
        case 'f': {
            if (attemptFold()) {
                break;
            }
            attemptCheck();
            break;
        }
        case 'ArrowUp': {
            increaseBet();
            break;
        }
        case 'ArrowRight': {
            increaseBet();
            break;
        }
        case 'ArrowDown': {
            decreaseBet();
            break;
        }
        case 'ArrowLeft': {
            decreaseBet();
            break;
        }
    }
}
export const PixiStandardBetControls = ({ myTurnProperties }) => {
    const dispatch = useAppDispatch();
    const socketSend = useSocketSend();
    const instanceId = useInstanceId();
    const foldButtonBounds = useAppSelector((state) => state.gameDimensions.foldButtonBounds, shallowEqual);
    const checkButtonBounds = useAppSelector((state) => state.gameDimensions.checkButtonBounds, shallowEqual);
    const callButtonBounds = useAppSelector((state) => state.gameDimensions.callButtonBounds, shallowEqual);
    const betButtonBounds = useAppSelector((state) => state.gameDimensions.betButtonBounds, shallowEqual);
    const raiseButtonBounds = useAppSelector((state) => state.gameDimensions.raiseButtonBounds, shallowEqual);
    const allInButtonBounds = useAppSelector((state) => state.gameDimensions.allInButtonBounds, shallowEqual);
    const isKeyboardCapturingInputActive = useAppSelector((state) => state.common.isKeyboardCapturingInputActive);
    // attach bet convenience functions to window for test purposes:
    useEffect(() => {
        if (!isTestEnvironment()) {
            return;
        }
        const topWindow = getTopWindow();
        const instanceFns = topWindow.instanceFns?.[instanceId];
        if (!instanceFns) {
            return;
        }
        instanceFns.betOrRaise = (betOrRaiseSliderAmount) => {
            if (myTurnProperties.canBet) {
                // bet
                socketSend(createBetOutbound(myTurnProperties.betSequenceNumber, betOrRaiseSliderAmount));
            }
            else {
                // raise
                const raiseAmount = betOrRaiseSliderAmount - (myTurnProperties.betTotalAmountToCall - myTurnProperties.myTotalBetAmount);
                socketSend(createRaiseOutbound(myTurnProperties.betSequenceNumber, raiseAmount));
            }
        };
        instanceFns.goAllIn = () => {
            instanceFns.betOrRaise?.(myTurnProperties.betSliderMaxAmount);
        };
        instanceFns.setBetSliderAmount = (betOrRaiseSliderAmount) => {
            dispatch(thunkSetBetSliderAmount(betOrRaiseSliderAmount));
        };
    }, [dispatch, instanceId, myTurnProperties, socketSend]);
    // remove bet convenience functions that were added to window for test purposes:
    useEffect(() => {
        return () => {
            if (!isTestEnvironment()) {
                return;
            }
            const topWindow = getTopWindow();
            const instanceFns = topWindow.instanceFns?.[instanceId];
            if (instanceFns) {
                delete instanceFns.betOrRaise;
                delete instanceFns.goAllIn;
                delete instanceFns.setBetSliderAmount;
            }
        };
    }, [instanceId]);
    useEventListener('keydown', (event) => {
        onKeyDown(event, myTurnProperties, isKeyboardCapturingInputActive, instanceId, socketSend, dispatch);
    });
    const { canFold, canCheck, canCall, canBet, canRaise, canGoAllIn, betSliderAmount, hasRangeOfPossibleBets } = myTurnProperties;
    return (_jsxs(Container, { "pixi-data": PIXI_DATA, x: 0, y: 0, children: [!isConfigFlagEnabled('domBetButtons') && canFold && foldButtonBounds && (_jsx(PixiFoldButton, { myTurnProperties: myTurnProperties, bounds: foldButtonBounds })), !isConfigFlagEnabled('domBetButtons') && canCheck && checkButtonBounds && (_jsx(PixiCheckButton, { myTurnProperties: myTurnProperties, bounds: checkButtonBounds })), !isConfigFlagEnabled('domBetButtons') && canCall && callButtonBounds && (_jsx(PixiCallButton, { myTurnProperties: myTurnProperties, bounds: callButtonBounds })), !isConfigFlagEnabled('domBetButtons') && canBet && betButtonBounds && (_jsx(PixiBetButton, { myTurnProperties: myTurnProperties, betSliderAmount: betSliderAmount, bounds: betButtonBounds })), !isConfigFlagEnabled('domBetButtons') && canRaise && raiseButtonBounds && (_jsx(PixiRaiseButton, { myTurnProperties: myTurnProperties, betSliderAmount: betSliderAmount, bounds: raiseButtonBounds })), !isConfigFlagEnabled('domBetButtons') && canGoAllIn && allInButtonBounds && (_jsx(PixiAllInButton, { myTurnProperties: myTurnProperties, bounds: allInButtonBounds })), hasRangeOfPossibleBets && isConfigFlagEnabled('setBetButtons') && (_jsx(PixiSetBetButtonGroup, { myTurnProperties: myTurnProperties }))] }));
};
